import React from 'react';
import { Loader2 } from 'lucide-react';

// ===== BOTÃO =====
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: 'primario' | 'secundario' | 'perigo' | 'fantasma' | 'dourado';
  tamanho?: 'sm' | 'md' | 'lg';
  carregando?: boolean;
  icone?: React.ReactNode;
}

export function Button({
  children, variante = 'primario', tamanho = 'md',
  carregando, icone, className = '', disabled, ...props
}: ButtonProps) {
  const estilos: Record<string, string> = {
    primario: 'btn-primario',
    secundario: 'btn-secundario',
    perigo: 'btn-perigo',
    fantasma: 'btn-fantasma',
    dourado: 'btn-dourado',
  };
  const tamanhos: Record<string, string> = {
    sm: 'btn-sm',
    md: 'btn-md',
    lg: 'btn-lg',
  };
  return (
    <button
      className={`btn ${estilos[variante]} ${tamanhos[tamanho]} ${className}`}
      disabled={disabled || carregando}
      {...props}
    >
      {carregando ? <Loader2 size={16} className="spin-icon" /> : icone}
      {children}
    </button>
  );
}

// ===== INPUT =====
interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  erro?: string;
  icone?: React.ReactNode;
}
export function Input({ label, erro, icone, className = '', id, ...props }: InputProps) {
  const inputId = id || label?.toLowerCase().replace(/\s/g, '_');
  return (
    <div className="campo-grupo">
      {label && <label htmlFor={inputId} className="campo-label">{label}</label>}
      <div className="campo-wrapper">
        {icone && <span className="campo-icone">{icone}</span>}
        <input
          id={inputId}
          className={`campo-input ${icone ? 'com-icone' : ''} ${erro ? 'com-erro' : ''} ${className}`}
          {...props}
        />
      </div>
      {erro && <span className="campo-erro">{erro}</span>}
    </div>
  );
}

// ===== SELECT =====
interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  erro?: string;
  opcoes: { value: string; label: string }[];
  placeholder?: string;
}
export function Select({ label, erro, opcoes, placeholder, className = '', id, ...props }: SelectProps) {
  const selectId = id || label?.toLowerCase().replace(/\s/g, '_');
  return (
    <div className="campo-grupo">
      {label && <label htmlFor={selectId} className="campo-label">{label}</label>}
      <select
        id={selectId}
        className={`campo-input ${erro ? 'com-erro' : ''} ${className}`}
        {...props}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {opcoes.map(op => (
          <option key={op.value} value={op.value}>{op.label}</option>
        ))}
      </select>
      {erro && <span className="campo-erro">{erro}</span>}
    </div>
  );
}

// ===== TEXTAREA =====
interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  erro?: string;
}
export function Textarea({ label, erro, className = '', id, ...props }: TextareaProps) {
  const taId = id || label?.toLowerCase().replace(/\s/g, '_');
  return (
    <div className="campo-grupo">
      {label && <label htmlFor={taId} className="campo-label">{label}</label>}
      <textarea
        id={taId}
        className={`campo-input ${erro ? 'com-erro' : ''} ${className}`}
        {...props}
      />
      {erro && <span className="campo-erro">{erro}</span>}
    </div>
  );
}

// ===== BADGE =====
interface BadgeProps {
  texto: string;
  cor?: 'azul' | 'verde' | 'dourado' | 'cinza' | 'vermelho';
}
export function Badge({ texto, cor = 'azul' }: BadgeProps) {
  return <span className={`badge badge-${cor}`}>{texto}</span>;
}

// ===== CARD =====
interface CardProps {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  hover?: boolean;
}
export function Card({ children, className = '', onClick, hover }: CardProps) {
  return (
    <div
      className={`card ${hover ? 'card-hover' : ''} ${className}`}
      onClick={onClick}
      style={onClick ? { cursor: 'pointer' } : undefined}
    >
      {children}
    </div>
  );
}

// ===== MODAL =====
interface ModalProps {
  aberto: boolean;
  onFechar: () => void;
  titulo: string;
  children: React.ReactNode;
  largura?: 'sm' | 'md' | 'lg';
}
export function Modal({ aberto, onFechar, titulo, children, largura = 'md' }: ModalProps) {
  React.useEffect(() => {
    if (aberto) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [aberto]);

  if (!aberto) return null;
  return (
    <div className="modal-overlay" onClick={onFechar}>
      <div
        className={`modal-caixa modal-${largura}`}
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 className="modal-titulo">{titulo}</h3>
          <button className="modal-fechar" onClick={onFechar} aria-label="Fechar">✕</button>
        </div>
        <div className="modal-corpo">{children}</div>
      </div>
    </div>
  );
}

// ===== PAGINAÇÃO =====
interface PaginacaoProps {
  paginaAtual: number;
  totalPaginas: number;
  onMudar: (pagina: number) => void;
}
export function Paginacao({ paginaAtual, totalPaginas, onMudar }: PaginacaoProps) {
  if (totalPaginas <= 1) return null;
  const paginas: (number | '...')[] = [];
  if (totalPaginas <= 7) {
    for (let i = 1; i <= totalPaginas; i++) paginas.push(i);
  } else {
    paginas.push(1);
    if (paginaAtual > 3) paginas.push('...');
    for (let i = Math.max(2, paginaAtual - 1); i <= Math.min(totalPaginas - 1, paginaAtual + 1); i++) paginas.push(i);
    if (paginaAtual < totalPaginas - 2) paginas.push('...');
    paginas.push(totalPaginas);
  }
  return (
    <div className="paginacao">
      <button
        className="pag-btn"
        disabled={paginaAtual === 1}
        onClick={() => onMudar(paginaAtual - 1)}
      >‹</button>
      {paginas.map((p, i) =>
        p === '...'
          ? <span key={`e${i}`} className="pag-reticencias">…</span>
          : <button
              key={p}
              className={`pag-btn ${p === paginaAtual ? 'ativo' : ''}`}
              onClick={() => onMudar(p as number)}
            >{p}</button>
      )}
      <button
        className="pag-btn"
        disabled={paginaAtual === totalPaginas}
        onClick={() => onMudar(paginaAtual + 1)}
      >›</button>
    </div>
  );
}

// ===== LOADING =====
export function Loading({ mensagem = 'Carregando...' }: { mensagem?: string }) {
  return (
    <div className="loading-centro">
      <div className="spinner" />
      <p className="loading-texto">{mensagem}</p>
    </div>
  );
}

// ===== VAZIO =====
export function Vazio({ icone, titulo, descricao }: { icone?: React.ReactNode; titulo: string; descricao?: string }) {
  return (
    <div className="vazio-centro">
      {icone && <div className="vazio-icone">{icone}</div>}
      <h3 className="vazio-titulo">{titulo}</h3>
      {descricao && <p className="vazio-descricao">{descricao}</p>}
    </div>
  );
}
